import { css } from 'lit';

export const styles = css`
  :host {
    display: flex;
    flex-direction: var(--tt-combobox-flex-direction, row);
    align-items: var(--tt-combobox-align-items, center);
    justify-content: var(--tt-combobox-justify-content, initial);
    align-content: var(--tt-combobox-align-content, initial);
    gap: var(--tt-combobox-gap, 0.5rem);
    font-size: var(--tt-combobox-font-size, var(--font-size-200));
    color: var(--tt-combobox-color, var(--color-text-400));
  }

  :host([disabled]) .tt-combobox-container {
    border-color: var(--tt-combobox-disabled-border-color, var(--color-border-200));
    color: var(--tt-combobox-disabled-color, var(--color-text-200));
    background-color: var(--tt-combobox-disabled-background-color, var(--color-surface-200));
    pointer-events: none;
  }

  .tt-combobox-container:focus-within {
    outline: 5px auto Highlight;
    outline: 5px auto -webkit-focus-ring-color;
  }

  .tt-combobox-container:hover {
    background-color: var(--tt-combobox-hover-background-color, var(--color-surface-300));
  }

  :host([invalid]) .tt-combobox-container,
  :host(:state(interacted):invalid) .tt-combobox-container {
    outline: 1px solid var(--color-alert-strong);
  }

  .errormessage {
    visibility: hidden;
    display: flex;
    align-items: center;
    gap: var(--space-scale-0-5);
    color: var(--color-alert-strong);

    &[data-hidden] {
      display: none;
    }
  }

  .errormessage svg path {
    fill: var(--color-alert-strong);
    height: 20px;
  }

  .tt-combobox-container:has([role='combobox'][aria-invalid='true']) ~ .errormessage {
    visibility: visible;
  }

  :has([role='combobox'][aria-invalid='true']) ::slotted([slot='error']) {
    color: var(--color-alert-strong);
    font-size: var(--font-size-100);
    margin: 0;
    font-weight: var(--font-weight-regular);
    line-height: 1.2;
  }

  slot[name='option']::slotted(*) {
    display: none;
  }

  * {
    box-sizing: border-box;
    font-family: var(--font-family-sans);
    cursor: inherit;
  }

  .tt-combobox-container {
    position: relative;
    max-width: var(--tt-combobox-max-width, 300px);
    min-width: var(--tt-combobox-min-width, 250px);
    display: flex;
    flex-direction: row;
    align-items: center;
    border-radius: var(--border-radius);
    border-color: var(--tt-combobox-border-color, var(--color-border-400));
    border-style: solid;
    border-width: var(--tt-combobox-border-width, 1px);
    background-color: var(--tt-combobox-background-color, var(--color-surface-100));
    padding: 0.5rem;
    gap: 0.25rem;
    //width: 100%;
  }

  [role='listbox'] {
    display: none;
  }

  [role='combobox'] {
    width: 100%;
    border-style: none;
    background-color: transparent;
    font-size: var(--tt-combobox-font-size, var(--font-size-200));
  }

  .hide-caret {
    caret-color: transparent;
  }

  [role='combobox']::placeholder {
    color: var(--tt-combobox-placeholder-color, var(--color-text-300));
    font-family: var(--font-family-inter);
    font-size: var(--tt-combobox-font-size, var(--font-size-200));
  }

  :host([disabled]) [role='combobox']::placeholder {
    color: var(--tt-combobox-disabled-placeholder-color, var(--color-text-200));
  }

  [role='combobox']:placeholder-shown {
    text-overflow: ellipsis;
    overflow: clip;
  }

  [role='combobox']:focus {
    outline: none;
  }

  [role='combobox'] ~ button {
    appearance: none;
    padding: 0;
    border-width: 0;
    background-color: transparent;
    aspect-ratio: 1;
  }

  [role='combobox'] ~ button svg {
    transition: transform 0.2s ease-in-out;
    color: var(--tt-combobox-dropdown-color, var(--color-text-400));
  }

  [role='combobox'][aria-expanded='true'] ~ button svg {
    transform: rotate(180deg);
  }

  [role='combobox'][aria-expanded='true'] ~ [role='listbox'] {
    display: block;
    width: max-content;
    max-width: var(--tt-combobox-list-max-width, 35ch);
    min-width: 100%;
    background-color: var(--tt-combobox-list-background-color, var(--color-surface-100, white));
    border: 1px solid var(--tt-combobox-border-color, var(--color-border-300));
    border-radius: var(--border-radius);
    box-shadow: var(--box-shadow-lg);
    padding: 0;
    position: absolute;
    top: var(--tt-combobox-top, calc(100% + 0.5rem));
    left: var(--tt-combobox-left, 0);
    right: var(--tt-combobox-right, unset);
    bottom: var(--tt-combobox-bottom, unset);
    z-index: 2;
    margin: 0;
    list-style: none;
    max-height: var(--tt-combobox-max-height, 400px);
    overflow-y: auto;

    .no-results {
      display: none;
    }

    &:not(:has(tt-option:not([hidden], .select-all))) {
      .no-results {
        display: flex;
        padding: 0.5rem;
        box-sizing: border-box;
        text-align: left;
        text-overflow: ellipsis;
        text-wrap: nowrap;
        align-items: center;
        gap: 0.25rem;
        max-width: 100%;
        width: 100%;
        overflow-y: visible;
        flex: 1;

        span {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          flex: 1;
          line-height: 1.1;
        }
      }

      .select-all {
        display: none;
      }
    }
  }

  [role='combobox'][aria-expanded='true'] ~ [role='listbox'][data-open-upward] {
    top: unset;
    bottom: calc(100% + 0.5rem);
  }

  slot[name='icon'] {
    display: inline-block;
    max-width: var(--tt-combobox-icon-size, 1rem);
    aspect-ratio: 1;
  }

  tt-option.select-all::part(option) {
    border-bottom: 1px solid var(--color-border-300);
  }

  label {
    font-size: var(--tt-combobox-label-font-size, inherit);
    color: var(--tt-combobox-label-color, inherit);
    font-weight: var(--tt-combobox-label-font-weight, inherit);

    &[data-hidden] {
      display: none;
    }
  }
`;
